import { useCallback, useEffect, useState } from 'react';

import type { ReportGroupBy } from '../types';
import { getChartPageSize } from '../utils/chartPageSize';
import { clampPage, getPageItems } from '../utils/pagination';
import { useElementWidth } from './useElementWidth';

/**
 * Pages a period series so it fits the chart width. Attach `ref` to the chart container;
 * the page resets whenever `resetKey` or the page size changes.
 */
export const useChartPagination = (total: number, groupBy: ReportGroupBy, resetKey?: unknown) => {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const [page, setPage] = useState(0);
  const pageSize = getChartPageSize(width, groupBy);
  const pageCount = Math.ceil(total / pageSize);
  const currentPage = clampPage(page, pageCount);

  useEffect(() => {
    setPage(0);
  }, [resetKey, pageSize]);

  const paginate = useCallback(
    <T,>(items: T[]): T[] => getPageItems(items, currentPage, pageSize),
    [currentPage, pageSize],
  );

  return {
    ref,
    page: currentPage,
    pageSize,
    pageCount,
    total,
    setPage,
    paginate,
  };
};

export type ChartPagination = ReturnType<typeof useChartPagination>;
